import axiosInstance from '@/app/api/axiosInstance';
import { SalesOrder, SalesOrderCreate, SalesOrderUpdate, SalesOrderListItem } from '@/app/types/salesOrder';
import { PaginatedResponse, ListQueryParams } from '@/app/types/api';

// Helper to parse numeric fields (like Decimal from backend)
const parseSalesOrderResponse = (salesOrder: SalesOrder): SalesOrder => ({
  ...salesOrder,
  total_amount: salesOrder.total_amount ? String(salesOrder.total_amount) : '0',
  items: salesOrder.items.map(item => ({
    ...item,
    quantity: item.quantity ? String(item.quantity) : '0',
    rate: item.rate ? String(item.rate) : '0',
    discount_percent: item.discount_percent ? String(item.discount_percent) : '0',
    discount_amount: item.discount_amount ? String(item.discount_amount) : '0',
    tax_percent: item.tax_percent ? String(item.tax_percent) : '0',
    tax_amount: item.tax_amount ? String(item.tax_amount) : '0',
    total_amount: item.total_amount ? String(item.total_amount) : '0',
    description: item.description || '',
    // Preserve the nested item object if it exists
    item: item.item || null,
  })),
});

// Helper to parse list item numeric fields (like Decimal from backend)
const parseSalesOrderListItem = (item: any): SalesOrderListItem => {
  // Calculate total_amount from items since API doesn't provide it at document level
  const calculatedTotal = item.items?.reduce((sum: number, docItem: any) => {
    return sum + parseFloat(docItem.total_amount || '0');
  }, 0) || 0;

  return {
    ...item,
    total_amount: String(calculatedTotal),
  };
};

export const listSalesOrders = async (
  params: ListQueryParams,
): Promise<PaginatedResponse<SalesOrderListItem>> => {
  try {
    const response = await axiosInstance.get<PaginatedResponse<any>>('/sales-orders/', { params });
    response.data.items = response.data.items.map(parseSalesOrderListItem);
    return response.data;
  } catch (error) {
    console.error('Failed to list sales orders:', error);
    throw error;
  }
};

export const getSalesOrder = async (id: number): Promise<SalesOrder> => {
  try {
    const response = await axiosInstance.get<SalesOrder>(`/sales-orders/${id}`);
    console.log('[salesOrderService] Raw API response:', response.data);
    return parseSalesOrderResponse(response.data);
  } catch (error) {
    console.error(`Failed to get sales order with ID ${id}:`, error);
    throw error;
  }
};

export const createSalesOrder = async (data: SalesOrderCreate): Promise<SalesOrder> => {
  try {
    // Ensure numeric fields are sent correctly (as strings if backend expects Decimal)
    const payload = {
      ...data,
      transaction_date: data.transaction_date || data.date, // Fallback to date if transaction_date not provided
      items: data.items.map(item => ({
        ...item,
        quantity: String(item.quantity),
        rate: String(item.rate),
        unit_id: item.unit_id,
        ...(item.discount_percent !== undefined && { discount_percent: String(item.discount_percent) }),
        ...(item.discount_amount !== undefined && { discount_amount: String(item.discount_amount) }),
        ...(item.tax_percent !== undefined && { tax_percent: String(item.tax_percent) }),
        ...(item.tax_amount !== undefined && { tax_amount: String(item.tax_amount) }),
        ...(item.total_amount !== undefined && { total_amount: String(item.total_amount) }),
        ...(item.description !== undefined && { description: item.description }),
      })),
    };
    
    const response = await axiosInstance.post<SalesOrder>('/sales-orders/', payload);
    return parseSalesOrderResponse(response.data);
  } catch (error) {
    console.error('Failed to create sales order:', error);
    throw error;
  }
};

export const updateSalesOrder = async (id: number, data: SalesOrderUpdate): Promise<SalesOrder> => {
  console.log(`updateSalesOrder called with id: ${id}`, data);
  try {
    // Ensure numeric fields are sent correctly (as strings if backend expects Decimal)
    const payload = {
      ...data,
      transaction_date: data.transaction_date || data.date, // Fallback to date if transaction_date not provided
      ...(data.items && {
        items: data.items.map(item => ({
          ...item,
          quantity: String(item.quantity),
          rate: String(item.rate),
          unit_id: item.unit_id,
          ...(item.discount_percent !== undefined && { discount_percent: String(item.discount_percent) }),
          ...(item.discount_amount !== undefined && { discount_amount: String(item.discount_amount) }),
          ...(item.tax_percent !== undefined && { tax_percent: String(item.tax_percent) }),
          ...(item.tax_amount !== undefined && { tax_amount: String(item.tax_amount) }),
          ...(item.total_amount !== undefined && { total_amount: String(item.total_amount) }),
          ...(item.description !== undefined && { description: item.description }),
        })),
      }),
    };
    
    console.log(`Making API request to update sales order ${id}...`);
    const response = await axiosInstance.patch<SalesOrder>(`/sales-orders/${id}`, payload);
    console.log(`Update API request successful, response:`, response.data);
    return parseSalesOrderResponse(response.data);
  } catch (error) {
    console.error(`Failed to update sales order with ID ${id}:`, error);
    throw error;
  }
};

export const deleteSalesOrder = async (id: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/sales-orders/${id}`);
  } catch (error) {
    console.error(`Failed to delete sales order with ID ${id}:`, error);
    throw error;
  }
};

export const convertSalesOrderToInvoice = async (id: number): Promise<{ id: number; document_number: string }> => {
  try {
    const response = await axiosInstance.post<{ id: number; document_number: string }>(`/sales-orders/${id}/convert-to-invoice`);
    return response.data;
  } catch (error) {
    console.error(`Failed to convert sales order ${id} to invoice:`, error);
    throw error;
  }
}; 